import React, { useEffect, useState } from 'react';
import { Container, Navbar, Table, Button, Row, Col, Card, Form, Modal, Tabs, Tab, Badge, CloseButton } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import api from '../api/client';
import VideoPlayer from '../components/VideoPlayer';

const DashboardPage = () => {
  const { logout, user } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [groups, setGroups] = useState<any[]>([]);
  const [subs, setSubs] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [liveStreams, setLiveStreams] = useState<any[]>([]);
  
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteUses, setInviteUses] = useState(1);
  const [newInviteCode, setNewInviteCode] = useState('');
  const [filterAction, setFilterAction] = useState('');

  const [selectedStream, setSelectedStream] = useState<any>(null);
  const [playData, setPlayData] = useState<any>(null);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = () => {
    fetchUsers();
    fetchGroups();
    fetchSubscriptions();
    fetchAuditLogs();
    fetchLiveStreams();
  };

  const fetchUsers = async () => {
    try { const res = await api.get('/admin/users'); setUsers(res.data); } catch (e) { console.error(e); }
  };

  const fetchGroups = async () => {
    try { const res = await api.get('/admin/groups'); setGroups(res.data); } catch (e) { console.error(e); }
  };

  const fetchSubscriptions = async () => {
    try { const res = await api.get('/admin/subscriptions'); setSubs(res.data); } catch (e) { console.error(e); }
  };

  const fetchLiveStreams = async () => {
    try { const res = await api.get('/streams/live'); setLiveStreams(res.data); } catch (e) { console.error(e); }
  };

  const fetchAuditLogs = async () => {
    try {
      const params = filterAction ? { action: filterAction } : {};
      const res = await api.get('/admin/audit', { params });
      setAuditLogs(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const toggleUserStatus = async (id: string, currentStatus: boolean) => {
    try {
      await api.patch(`/admin/users/${id}`, { is_disabled: !currentStatus });
      fetchUsers();
    } catch (e) {
      console.error(e);
    }
  };

  const changeUserRole = async (id: string, newRole: string) => {
    try {
      await api.patch(`/admin/users/${id}`, { role: newRole });
      fetchUsers();
    } catch (e) {
      console.error(e);
    }
  };

  const createInvite = async () => {
    try {
      const res = await api.post('/admin/invites', { uses: inviteUses });
      setNewInviteCode(res.data.code);
    } catch (e) {
      console.error(e);
    }
  };

  const watchStream = async (stream: any) => {
    try {
      const res = await api.post(`/streams/${stream.id}/play-token`);
      setPlayData(res.data);
      setSelectedStream(stream);
    } catch (e) {
      alert('Failed to get play token');
    }
  };

  const videoJsOptions = playData ? {
    autoplay: true,
    controls: true,
    responsive: true,
    fluid: true,
    sources: [{
      src: `${playData.hlsUrl}?token=${playData.playToken}`,
      type: 'application/x-mpegURL'
    }]
  } : null;

  return (
    <>
      <Navbar bg="dark" variant="dark" expand="lg" sticky="top">
        <Container>
          <Navbar.Brand href="#">PocketCast Admin</Navbar.Brand>
          <Navbar.Collapse className="justify-content-end">
            <Navbar.Text className="me-3 d-none d-sm-inline">Signed in as: {user?.username}</Navbar.Text>
            <Button variant="outline-light" size="sm" onClick={logout}>Logout</Button>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <Container className="mt-4 pb-5">
        <Tabs defaultActiveKey="live" className="mb-4 shadow-sm p-2 bg-white rounded">
          <Tab eventKey="live" title={<span>Live Now <Badge bg="danger">{liveStreams.length}</Badge></span>}>
            <Row>
              <Col md={selectedStream ? 8 : 12}>
                <Card className="mb-4 shadow-sm border-0">
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h3>Active Streams</h3>
                      <Button variant="outline-primary" onClick={fetchLiveStreams}>Refresh</Button>
                    </div>
                    <Table striped bordered hover responsive>
                      <thead className="table-dark">
                        <tr>
                          <th>Title</th>
                          <th>Streamer</th>
                          <th>Started</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {liveStreams.map((s) => (
                          <tr key={s.id} className={selectedStream?.id === s.id ? 'table-primary' : ''}>
                            <td>{s.title}</td>
                            <td>{s.streamer?.username}</td>
                            <td>{new Date(s.started_at).toLocaleTimeString()}</td>
                            <td>
                              <Button variant="success" size="sm" onClick={() => watchStream(s)}>Watch</Button>
                            </td>
                          </tr>
                        ))}
                        {liveStreams.length === 0 && <tr><td colSpan={4} className="text-center">No active streams</td></tr>}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
              {selectedStream && (
                <Col md={4}>
                  <Card className="shadow-sm border-0">
                    <Card.Header className="bg-dark text-white d-flex justify-content-between align-items-center">
                      <span>Watching: {selectedStream.title}</span>
                      <CloseButton variant="white" onClick={() => setSelectedStream(null)} />
                    </Card.Header>
                    <Card.Body className="p-0 bg-black">
                      {videoJsOptions && <VideoPlayer options={videoJsOptions} />}
                    </Card.Body>
                  </Card>
                </Col>
              )}
            </Row>
          </Tab>

          <Tab eventKey="users" title="Users">
            <Card className="mb-4 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3>User Management</h3>
                  <div className="d-flex gap-2">
                    <Button variant="primary" onClick={() => { setNewInviteCode(''); setShowInviteModal(true); }}>Generate Invite</Button>
                    <Button variant="outline-primary" onClick={fetchUsers}>Refresh</Button>
                  </div>
                </div>
                <Table striped bordered hover responsive className="shadow-sm">
                  <thead className="table-dark">
                    <tr>
                      <th>Username</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status / Actions</th>
                      <th>Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.username}</td>
                        <td>{u.email}</td>
                        <td>
                          <Form.Select 
                            size="sm" 
                            value={u.role} 
                            onChange={(e) => changeUserRole(u.id, e.target.value)}
                            disabled={u.id === user.id}
                          >
                            <option value="spectator">spectator</option>
                            <option value="streamer">streamer</option>
                            <option value="admin">admin</option>
                          </Form.Select>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {u.is_disabled ? <span className="text-danger small">Disabled</span> : <span className="text-success small">Active</span>}
                            <Button 
                              variant={u.is_disabled ? "outline-success" : "outline-warning"} 
                              size="sm" 
                              onClick={() => toggleUserStatus(u.id, u.is_disabled)}
                              disabled={u.id === user.id}
                            >
                              {u.is_disabled ? 'Enable' : 'Disable'}
                            </Button>
                          </div>
                        </td>
                        <td>{new Date(u.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="groups" title="Groups">
            <Card className="mb-4 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3>Groups</h3>
                  <Button variant="outline-primary" onClick={fetchGroups}>Refresh</Button>
                </div>
                <Table striped bordered hover responsive>
                  <thead className="table-dark">
                    <tr>
                      <th>Name</th>
                      <th>Owner</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <tr key={g.id}>
                        <td>{g.name}</td>
                        <td>{g.owner?.username}</td>
                        <td>{new Date(g.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="subscriptions" title="Subscriptions">
            <Card className="mb-4 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3>Subscriptions (Viewer to Streamer)</h3>
                  <Button variant="outline-primary" onClick={fetchSubscriptions}>Refresh</Button>
                </div>
                <Table striped bordered hover responsive>
                  <thead className="table-dark">
                    <tr>
                      <th>Viewer</th>
                      <th>Streamer</th>
                      <th>Status</th>
                      <th>Requested At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subs.map((s) => (
                      <tr key={s.id}>
                        <td>{s.subscriber?.username}</td>
                        <td>{s.streamer?.username}</td>
                        <td><span className={`badge ${s.status === 'approved' ? 'bg-success' : 'bg-warning'}`}>{s.status}</span></td>
                        <td>{new Date(s.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="audit" title="Audit Logs">
            <Card className="mb-4 shadow-sm border-0">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h3>Audit Activity</h3>
                  <div className="d-flex gap-2">
                    <Form.Control 
                      size="sm" 
                      placeholder="Action (e.g. LOGIN_SUCCESS)" 
                      value={filterAction} 
                      onChange={(e) => setFilterAction(e.target.value)} 
                      style={{ width: '200px' }}
                    />
                    <Button variant="primary" size="sm" onClick={fetchAuditLogs}>Filter</Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => { setFilterAction(''); fetchAuditLogs(); }}>Reset</Button>
                  </div>
                </div>
                <Table striped bordered hover responsive size="sm">
                  <thead className="table-dark">
                    <tr>
                      <th>Timestamp</th>
                      <th>Actor</th>
                      <th>Action</th>
                      <th>Target</th>
                      <th>IP / Context</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{new Date(log.created_at).toLocaleString()}</td>
                        <td>{log.actor?.username || 'System'}</td>
                        <td><code className="text-dark">{log.action}</code></td>
                        <td><small>{log.target_id}</small></td>
                        <td><small>{log.ip || log.user_agent}</small></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>

      <Modal show={showInviteModal} onHide={() => setShowInviteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Generate New Invite</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {newInviteCode ? (
            <div className="text-center p-4">
              <h4>New Invite Code:</h4>
              <h2 className="text-primary my-3" style={{ letterSpacing: '2px' }}>{newInviteCode}</h2>
              <p className="text-muted small">Share this code with the user.</p>
            </div>
          ) : (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Number of Allowed Registrations</Form.Label>
                <Form.Control 
                  type="number" 
                  value={inviteUses} 
                  onChange={(e) => setInviteUses(parseInt(e.target.value))} 
                  min="1" 
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          {!newInviteCode && <Button variant="primary" onClick={createInvite}>Generate</Button>}
          <Button variant="secondary" onClick={() => setShowInviteModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DashboardPage;
